import React, { PropsWithoutRef, useEffect, useState } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { SIDEBAR_WIDTH } from "../constants";
import { usePermission } from "../hooks/usePermission";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import PaymentsIcon from "@mui/icons-material/Payments";
import PaidIcon from "@mui/icons-material/Paid";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SourceIcon from "@mui/icons-material/Source";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

/* ---------------- CONFIG ---------------- */

const sidebarConfig = [
  {
    id: "dashboard",
    label: "Dashboard",
    arLabel: "لوحة المعلومات",
    icon: <DashboardIcon />,
    path: "/dashboard",
  },
  {
    id: "customers",
    label: "Customer Management",
    arLabel: "إدارة العملاء",
    icon: <PeopleAltIcon />,
    path: "/manage-users",
  },
  // {
  //   id: "dealers",
  //   label: "Dealers Management",
  //   arLabel: "إدارة الوكلاء",
  //   icon: <WarehouseIcon />,
  //   path: "/manage-dealers",
  // },
  {
    id: "categories",
    label: "Categories Management",
    arLabel: "إدارة الفئات",
    icon: <img src="/static/images/logs_icon.svg" alt="Icon" />,
    path: "/manage-categories",
  },
  {
    id: "listings",
    label: "Listings Management",
    arLabel: "إدارة القوائم",
    icon: <DirectionsCarFilledIcon />,
    children: [
      { label: "Listed Vehicles", arLabel: "المركبات المدرجة", path: "/manage-vehicles" },
      { label: "Listed Spare Parts", arLabel: "قطع الغيار المدرجة", path: "/manage-spare-parts" },
      { label: "Listed Accessories", arLabel: "الملحقات المدرجة", path: "/manage-car-accessories" },
    ],
  },
  {
    id: "specification",
    label: "Vehicle Specification",
    arLabel: "مواصفات المركبة",
    icon: <SettingsApplicationsIcon />,
    children: [
      { id: "brands", label: "Manage Brands", arLabel: "إدارة العلامات التجارية", path: "/manage-brands" },
      { id: "model", label: "Manage Model", arLabel: "إدارة الموديلات", path: "/manage-model" },
      { id: "variant", label: "Manage Variant", arLabel: "إدارة المتغيرات", path: "/manage-variant" },
      // { id: "vehicle-type", label: "Manage Vehicle Type", path: "/manage-vehicle-type" },
      { id: "fuel-type", label: "Manage Fuel Type", arLabel: "إدارة نوع الوقود", path: "/manage-fuel-type" },
      { id: "engine-power", label: "Manage Engine Power", arLabel: "إدارة قوة المحرك", path: "/manage-engine-power" },
      { id: "transmission", label: "Manage Transmission", arLabel: "إدارة ناقل الحركة", path: "/manage-transmission" },
      { id: "drive-type", label: "Manage Drive Type", arLabel: "إدارة نوع الدفع", path: "/manage-drive-type" },
      { id: "emissions-class", label: "Manage Emissions Class", arLabel: "إدارة فئة الانبعاثات", path: "/manage-emissions-class" },
      { id: "environmental-badge", label: "Manage Environmental Badge", arLabel: "إدارة الشارة البيئية", path: "/manage-environmental-badge" },
      { id: "damage-type", label: "Manage Damage Type", arLabel: "إدارة نوع الضرر", path: "/manage-damage-type" },
      // { id: "condition", label: "Manage Condition", path: "/manage-condition" },
      { id: "axles", label: "Manage Axles", arLabel: "إدارة المحاور", path: "/manage-axles" },
      { id: "color", label: "Manage Color", arLabel: "إدارة الألوان", path: "/manage-color" },
      { id: "features", label: "Manage Features", arLabel: "إدارة الميزات", path: "/manage-features" },
    ],
  },
  {
    id: "packages",
    label: "Manage Packages",
    arLabel: "إدارة الباقات",
    icon: <PaidIcon />,
    path: "/manage-packages",
  },
  {
    id: "revenue",
    label: "Revenue Management",
    arLabel: "إدارة الإيرادات",
    icon: <PaymentsIcon />,
    path: "/manage-revenue",
  },
  {
    id: "analytics",
    label: "Report & Analytics",
    arLabel: "التقارير والتحليلات",
    icon: <AnalyticsIcon />,
    path: "/manage-analytics",
  },
  {
    id: "notifications",
    label: "Notification Management",
    arLabel: "إدارة الإشعارات",
    icon: <NotificationsIcon />,
    path: "/manage-notifications",
  },
  {
    id: "subadmin",
    label: "Sub-Admin Management",
    arLabel: "إدارة المشرفين الفرعيين",
    icon: <ManageAccountsIcon />,
    path: "/manage-subadmin",
  },
  {
    id: "blogs",
    label: "Blog Management",
    arLabel: "إدارة المدونة",
    icon: <SourceIcon />,
    path: "/manage-blogs-listings",
  },
  {
    id: "cms",
    label: "CMS Management",
    arLabel: "إدارة نظام إدارة المحتوى",
    icon: <img src="/static/images/cms_icon.svg" alt="Icon" />,
    path: "/manage-cms",
  },
];

/* ---------------- COMPONENT ---------------- */

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, isLoading } = usePermission();

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const filteredSidebarConfig = React.useMemo(() => {
    return sidebarConfig.filter(item => {
      // If item has children, show parent if any child is allowed (or if parent itself is allowed)
      // For simplicity and based on the provided API response structure, we check by label.
      const hasItemPermission = hasPermission(item.label, "isView");
      
      if (item.children) {
        // If it's a grouped menu, we might want to check children permissions too
        // but for now we follow the top-level label permission.
        return hasItemPermission;
      }
      
      return hasItemPermission;
    });
  }, [hasPermission]);

  const isRouteActive = React.useCallback(
    (path: string) =>
      location.pathname === path ||
      location.pathname.startsWith(path),
    [location.pathname]
  );

  const isAnyChildActive = React.useCallback(
    (children: any[]) => children.some((child) => isRouteActive(child.path)),
    [isRouteActive]
  );

  /* Auto expand active menus */
  useEffect(() => {
    const updated: Record<string, boolean> = {};

    filteredSidebarConfig.forEach((item) => {
      if (item.children) {
        updated[item.id] = isAnyChildActive(item.children);
      }
    });

    setOpenMenus((prev) => {
      const isChanged = Object.keys(updated).some(key => updated[key] !== prev[key]) || 
                        Object.keys(prev).some(key => updated[key] !== prev[key]);
      return isChanged ? updated : prev;
    });
  }, [location.pathname, isAnyChildActive, filteredSidebarConfig]);

  const drawer = (
    <>
      {/* LOGO */}
      <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate("/dashboard");
          }}
          className="logo_link"
        >
          <figure>
            <img
              src={mobileOpen ? "/static/images/logosmall.jpeg" : "/static/images/logobig.jpeg"}
              alt="Car Advertising"
            />
          </figure>
        </a>

        <IconButton
          sx={{ display: "none", position: "absolute", top: 10, right: 10 }}
          onClick={handleSidebarToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {isLoading ? (
          <div style={{ color: 'white', padding: '20px' }}>Loading...</div>
        ) : filteredSidebarConfig.map((item) => {
          const hasChildren = !!item.children;

          const isActive = item.path
            ? isRouteActive(item.path)
            : hasChildren
              ? isAnyChildActive(item.children!)
              : false;

          const isOpen = openMenus[item.id] || false;

          return (
            <React.Fragment key={item.id}>
              <ListItemButton
                className="lst-itm"
                selected={isActive}
                onClick={() => {
                  if (hasChildren) {
                    setOpenMenus((prev) => ({
                      ...prev,
                      [item.id]: !prev[item.id],
                    }));
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText className="lstitm-txt" primary={
                  <>
                    <span style={{ display: 'block' }}>{item.label}</span>
                    {item.arLabel && <span style={{ display: 'block', fontSize: '0.85em', color: '#888', marginTop: '4px' }}>{item.arLabel}</span>}
                  </>
                } />
                {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>

              {hasChildren && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children!.map((child) => (
                      <Tooltip
                        key={child.path}
                        title={child.label}
                        placement="right"
                      >
                        <ListItemButton
                          selected={isRouteActive(child.path)}
                          onClick={() => navigate(child.path)}
                          sx={{ pl: 4 }}
                        >
                          <ListItemText
                            className="lstitm-txt"
                            primary={
                              <>
                                <span style={{ display: 'block' }}>{child.label}</span>
                                {child.arLabel && <span style={{ display: 'block', fontSize: '0.85em', color: '#888', marginTop: '4px' }}>{child.arLabel}</span>}
                              </>
                            }
                          />
                        </ListItemButton>
                      </Tooltip>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: mobileOpen ? SIDEBAR_WIDTH : 0,
        transition: "width 0.3s",
        overflowX: "hidden",
      }}
      className={mobileOpen ? "sidebar-main-div active" : "sidebar-main-div"}
    >
      <Drawer
        className="sidebar_drawer"
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}